import moment from 'moment';
import type { Types, Warning } from '../types';
import type { OutTypes } from './schedules';

export function parseCalendarExceptions (
  period:    Types.periodGeneral | undefined,
  startYear: number,
  warnings:  Warning[]
): OutTypes.settings['calendarExceptions'] {
  if (!period) return [];

  // should warn only once
  let warned = false;

  // parse range
  const allowedRanges = (period.SchoolYear ?? '')
    .split(',')
    // parse each range
    .map(x => {
      // date ranges on the form "20/8-23/8"
      const [_start, _end] = x.split('-');
      if (!_start || !_end) return;

      const start = moment.utc(`${_start}/${startYear}`, 'DD/MM/YYYY').startOf('day');
      const end   = moment.utc(`${_end}/${startYear}`,   'DD/MM/YYYY').endOf  ('day');
      if (!start.isValid() || !end.isValid()) {
        if (warned) return;
        warned = true;
        warnings.push({
          code:    'calendarExceptions.problem_parsing_range',
          context: period
        });
        return;
      }

      return { start, end };
    })
    .filter((x): x is NonNullable<typeof x> => !!x)
    // make the ranges ordered by incrementing by a year if needed
    .reduce<{ start: moment.Moment, end: moment.Moment }[]>((acc, x) => {
      const prev = acc.at(-1);
      if (!prev) {
        acc.push(x);
      } else {
        while (prev.end.isAfter(x.start)) {
          x.start.add(1, 'year');
          x.end  .add(1, 'year');
        }
        acc.push(x);
      }
      return acc;
    }, []);

  // extend the range to whole week if the end is a friday
  for (let i = 1; i < allowedRanges.length - 1; i++) {
    const x = allowedRanges[i];

    if (x.end.isoWeekday() === 5) {
      x.end = x.end.add(2, 'days');
    }
  };

  // merge ranges if they are adjacent or overlapping (the latter is not expected)
  for (let i = 0; i < allowedRanges.length - 1; i++) {
    const x = allowedRanges[i];
    const y = allowedRanges[i + 1];

    const diff = y.start.diff(x.end);  // 1 ms if adjacent, negative if overlapping
    if (diff <= 2) {
      x.end = moment.max(x.end, y.end);
      allowedRanges.splice(i + 1, 1);
      i--;
    }
  }

  // invert the allowed ranges to get the exceptions
  const exceptions: { start: moment.Moment, end: moment.Moment }[] = [];
  for (let i = 0; i < allowedRanges.length - 1; i++) {
    const x = allowedRanges[i];
    const y = allowedRanges[i + 1];
    exceptions.push({
      start: x.end  .add     (1, 'day').startOf('day'),
      end:   y.start.subtract(1, 'day').endOf  ('day'),
    });
  }

  return exceptions.map(x => ({
    start: x.start.toISOString(),
    end:   x.end  .toISOString(),
  }));
}